import store from '../store';
import Vue from 'vue';
import router from "../router";
import api from '../utilities/api';

/**
 * Fetches profile and personal-profile data and stores it in the Vuex store
 * @returns {Promise} Resolves when profile data is loaded and stored
 */
function loadAndStoreProfileData() {
    return Promise.all([
        api.v2.get('profile'),
        api.v2.get('personal-profile')
    ]).then(([profileResp, personalProfileResp]) => {
        console.log('[AUTH] Profile data loaded');
        store.dispatch('userProfile/setProfile', profileResp.data);
        store.dispatch('userProfile/setPersonalProfile', personalProfileResp.data);
    }).catch((error) => {
        console.error('[AUTH] Failed to load profile data:', error);
        // Don't fail the auth flow if profile fetch fails
    });
}

export default {
    login(email, password) {
        console.log('[AUTH] Starting v2 login process');

        return api.v2.post('auth/login', { email, password, temporary: false })
            .then(response => {
                console.log('[AUTH] V2 login successful');

                // Store V2 token
                store.dispatch('authentication/storeV2Token', {
                    token: response.body.token
                });

                const roles = response.body.roles || [];
                const admin = roles.indexOf('Admin') > -1;

                // Store main login details (roles and admin status)
                return store.dispatch('authentication/storeLoginDetails', {
                    isAdmin: admin,
                    roles
                }).then(() => {
                    console.log('[AUTH] Login complete');
                    // Load and store profile data
                    return loadAndStoreProfileData().then(() => true);
                });
            })
            .catch((error) => {
                if (error.body?.requires2FA === true) {
                    throw {
                        requires2FA: true,
                        token: error.body.token,
                        mobile: error.body.mobile,
                        email: email,
                        password: password
                    };
                }
                const errorMessage = this.extractErrorMessage(error);
                throw errorMessage;
            });
    },
    
    extractErrorMessage(error) {
        // If we already have a message string from the API
        if (error.body?.message) {
            return error.body.message;
        }
        
        // Status 0 = Network/CORS/Browser blocking issue
        if (error.status === 0) {
            return 'Unable to connect to the server. Please try: 1) Disabling browser extensions (especially ad blockers), 2) Using a different browser or network, 3) Clearing your browser cache and cookies.';
        }
        
        // Server errors (500-599)
        if (error.status >= 500) {
            return 'The server is experiencing issues. Please try again in a few moments.';
        }
        
        // Authentication errors (401, 403)
        if (error.status === 401 || error.status === 403) {
            return 'Invalid email and password combination. Please try again.';
        }
        
        // Timeout
        if (error.status === 408 || error.statusText === 'timeout') {
            return 'Request timed out. Please check your internet connection and try again.';
        }
        
        // Generic fallback
        return 'Login failed. Please check your credentials and try again.';
    },
    impersonateUser(emailAddress) {
        console.log('[AUTH] Starting v2 impersonation');

        return api.v2.post(`auth/login/impersonate?user=${encodeURIComponent(emailAddress)}`)
            .then(response => {
                console.log('[AUTH] V2 impersonation successful');

                // Store V2 token
                store.dispatch('authentication/storeV2Token', {
                    token: response.body.token
                });

                const roles = response.body.roles || [];
                const admin = roles.indexOf('Admin') > -1;

                // Store main login details (roles and admin status)
                return store.dispatch('authentication/storeLoginDetails', {
                    isAdmin: admin,
                    roles
                }).then(() => {
                    console.log('[AUTH] Impersonation complete');
                    // Load and store profile data for impersonated user
                    return loadAndStoreProfileData().then(() => ({
                        success: true,
                        email: emailAddress,
                        token: response.body.token,
                        roles
                    }));
                });
            })
            .catch((error) => {
                throw error.body?.message || 'Failed to impersonate user';
            });
    },
    roles() {
        return store.state.authentication.roles;
    },
    v1Token() {
        return store.state.authentication.v1Token;
    },
    v2Token() {
        return store.state.authentication.v2Token;
    }
}
